import { ReactElement, useContext, useEffect, useState, useMemo } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';

import { useEvent, useEventDispatch } from '~/lib/events';

import StripeHelper from '~/lib/checkout/StripeHelper';
import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';
import { GlobalContext } from '~/contexts/global';
import { SelectedPaymentType } from '~/types/checkout';
import { currencyWithCents } from '~/lib/format';
import {
  isFreebie,
  canPayWithCredits,
  cartContainsPlusDeal,
} from '~/lib/checkout/util';
import {
  ADDITIONAL_PAYMENT_METHODS,
  CHECKOUT_ERRORS,
  COUNTRIES_REQUIRE_POSTAL_CODE,
  ERROR_TYPES,
  GLOBAL_ERROR_NAME,
  STEP,
  STRIPE_HELPER_KEYS,
} from '~/constants/checkout';
import { useCart } from '~/lib/cart';

import CheckoutTrust from '~/components/checkout/Global/CheckoutTrust';
import Credits from '~/components/checkout/Global/Credits';
import CheckoutButton from '~/components/checkout/Summary/CheckoutButton';
import { ViewCart } from '~/components/checkout/Summary/ViewCart';
import Discounts from '~/components/checkout/Summary/Discounts';
import DiscountsCouponExp from '~/components/checkout/Summary/DiscountsCouponExp';
import { CampaignIncentive } from '~/components/checkout/Summary/CampaignIncentive';

const { CREDITS, NEW_PAYMENT_METHOD } = ADDITIONAL_PAYMENT_METHODS;

export default function Summary({
  isCreditsCouponsEnabled,
}: Readonly<{
  isCreditsCouponsEnabled: boolean;
}>): ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const {
    campaigns: { showCampaignIncentive },
  } = useContext(GlobalContext);
  const dispatchEvent = useEventDispatch();
  const { mutateItems } = useCart();
  const {
    billingDetails,
    cart,
    paymentElementData,
    selectedPaymentMethod,
    step,
  } = useCheckoutState();
  const {
    setStepErrorMessage,
    setSubmittedPaymentMethod,
    submitPaymentMethod,
    setStepValidation,
    areAllStepsValid,
    setStep,
    refreshDiscountData,
  } = useCheckoutAPI();
  const [couponError, setCouponError] = useState('');
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [isCreditsLoading, setIsCreditsLoading] = useState(false);
  const [isPaymentPending, setIsPaymentPending] = useState(false);

  useEvent('credits:loading', () => setIsCreditsLoading(true));
  useEvent('credits:reset', () => setIsCreditsLoading(false));
  useEvent('coupon:loading', () => setIsCouponLoading(true));
  useEvent('coupon:reset', () => setIsCouponLoading(false));
  useEvent('checkout:submit', () => onSubmitPayment());

  const stripeHelper = useMemo(() => {
    return new StripeHelper(stripe, elements, cart ?? null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stripe) {
      stripeHelper.setValue(STRIPE_HELPER_KEYS.stripe, stripe);
    }

    if (elements) {
      stripeHelper.setValue(STRIPE_HELPER_KEYS.elements, elements);
    }

    if (cart) {
      stripeHelper.setValue(STRIPE_HELPER_KEYS.cart, cart);
    }
  }, [cart, stripe, elements]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isValid = false;

    if (canPayWithCredits(cart) || isFreebie(cart)) {
      isValid = true;
    } else if (selectedPaymentMethod === NEW_PAYMENT_METHOD) {
      isValid = paymentElementData.complete;
    } else {
      isValid = !!selectedPaymentMethod;
    }
    setStepValidation(STEP.PAYMENT, isValid);
  }, [
    selectedPaymentMethod,
    cart,
    setStepValidation,
    paymentElementData.complete,
  ]);

  useEffect(() => {
    if (cartContainsPlusDeal(cart)) {
      elements?.update({ paymentMethodTypes: ['card'] });
    } else {
      elements?.update({ paymentMethodTypes: [] });
    }
  }, [cart, elements]);

  const updatePaymentStatus = (status: boolean): void => {
    setIsPaymentPending(status);
    if (status) {
      dispatchEvent('payment:pending');
      return;
    }
    dispatchEvent('payment:reset');
  };

  const handleSubmitPayment = async (
    selectedPaymentMethod: SelectedPaymentType,
  ) => {
    let errMsg = CHECKOUT_ERRORS.GENERIC;

    try {
      const res = await submitPaymentMethod(selectedPaymentMethod);
      const { errorMessage, step, returnUrl } =
        await stripeHelper.handleServerResponse(res);

      if (errorMessage) {
        errMsg = errorMessage;
        setStepErrorMessage(step, errorMessage);
      } else if (returnUrl) {
        window.location.href = returnUrl;
        return;
      }

      updatePaymentStatus(false);
    } catch (e: any) {
      // Per linter must be any
      setSubmittedPaymentMethod(selectedPaymentMethod);
      updatePaymentStatus(false);

      try {
        if (e.response.status === 400) {
          const { error, type } = await e.response.json();

          if (type === ERROR_TYPES.PAYMENT) {
            setStepErrorMessage(STEP.PAYMENT, error);

            return;
          } else if (type === ERROR_TYPES.COUPON) {
            // This could have other errors besides an invalid coupon
            setCouponError(CHECKOUT_ERRORS.COUPON);

            if (isCreditsCouponsEnabled) {
              setStepErrorMessage(STEP.CREDITS_COUPONS, error);
              setStep(STEP.CREDITS_COUPONS);
              refreshDiscountData();
              // Ensure the cart is updated with the new coupon state
              mutateItems();

              return;
            }
          }

          errMsg = error;
        }
      } catch (error) {
        // Tried to parse json...
      }
    }

    setStepErrorMessage(GLOBAL_ERROR_NAME, errMsg);
  };

  async function onSubmitPayment(): Promise<void> {
    updatePaymentStatus(true);

    const isPayingWithCredits = canPayWithCredits(cart);
    let error = CHECKOUT_ERRORS.MISSING_PAYMENT_METHOD;
    let paymentToSubmit = null;

    if (
      selectedPaymentMethod === NEW_PAYMENT_METHOD &&
      !isPayingWithCredits &&
      !isFreebie(cart)
    ) {
      const { error: stripeError, paymentMethod } =
        await stripeHelper.createStripePaymentMethod(billingDetails);
      error = stripeError ?? error;
      paymentToSubmit = paymentMethod?.id as SelectedPaymentType;
    }

    // Check if we can use credits to completely pay for the invoice
    if (isPayingWithCredits) {
      paymentToSubmit = CREDITS;
    }

    if (
      paymentToSubmit ||
      isFreebie(cart) || // Freebie will not have a payment method
      selectedPaymentMethod?.startsWith('card') ||
      selectedPaymentMethod?.startsWith('pm')
    ) {
      paymentToSubmit = paymentToSubmit ?? selectedPaymentMethod;
      return handleSubmitPayment(paymentToSubmit);
    }

    // Missing a payment method, show error
    updatePaymentStatus(false);
    setStepErrorMessage(STEP.PAYMENT, error);
  }

  const isEmptyCart = !cart?.cart_items?.length;
  const isCheckoutDisabled =
    !areAllStepsValid(cart) ||
    isCouponLoading ||
    isCreditsLoading ||
    (step !== STEP.PAYMENT && !isFreebie(cart)) || // Skip payment step if freebie
    isPaymentPending ||
    isEmptyCart;
  const postalCodeText =
    COUNTRIES_REQUIRE_POSTAL_CODE.includes(
      `${billingDetails?.country?.value}`,
    ) && !!billingDetails.postalCode
      ? `, ${billingDetails.postalCode}`
      : '';

  const discountsEl = isCreditsCouponsEnabled ? (
    <DiscountsCouponExp cart={cart} />
  ) : (
    <Discounts
      couponError={couponError}
      cart={cart}
      isCouponLoading={isCouponLoading}
      setCouponError={setCouponError}
      isDisabled={isPaymentPending}
    />
  );

  return (
    <div className="mt-6 flex flex-col gap-6 md:mt-0">
      <ViewCart cart={cart} />

      {showCampaignIncentive && <CampaignIncentive className="md:hidden" />}

      <div className="flex flex-col gap-4 rounded bg-iceberg p-4">
        <div className="flex">
          <div className="grow">Subtotal</div>
          <div
            className="font-medium"
            data-testid="summary-checkout-cart-subtotal"
          >
            {currencyWithCents(cart?.subtotal)}
          </div>
        </div>
        {/* Do not show discounts if Freebie */}
        {!!cart?.subtotal && discountsEl}
        <div>
          <div className="flex">
            <div className="grow">Sales tax</div>
            <div
              className="font-medium"
              data-testid="summary-checkout-cart-tax"
            >
              {currencyWithCents(cart?.tax)}
            </div>
          </div>

          {billingDetails?.country?.label && (
            <div className="mt-2 text-grace">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mx-2" />
              {billingDetails.country.label}
              {postalCodeText}
            </div>
          )}
        </div>
        <div className="border-t border-sundog" />
        <Credits cart={cart} />
        <div className="flex font-bold">
          <div className="grow">Total:</div>
          <div data-testid="summary-checkout-cart-total">
            {currencyWithCents(cart?.total)}
          </div>
        </div>
      </div>
      <CheckoutButton
        isCheckoutDisabled={isCheckoutDisabled}
        isPaymentPending={isPaymentPending}
        onSubmitPayment={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          onSubmitPayment();
        }}
        selectedPaymentMethod={selectedPaymentMethod}
        isCredits={canPayWithCredits(cart)}
      />

      <CheckoutTrust />
    </div>
  );
}
